import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionReveal from "./SectionReveal";
import Divider from "./Divider";

// Target: 16 August 2026, 18:00 (local) — using ET
const WEDDING_DATE = new Date("2026-08-16T18:00:00-04:00").getTime();

function getRemaining() {
  const diff = Math.max(0, WEDDING_DATE - Date.now());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

function Digit({ value, label, testid }) {
  const padded = String(value).padStart(2, "0");
  return (
    <div
      className="flex flex-col items-center flex-1 min-w-0"
      data-testid={testid}
    >
      <div className="relative w-full">
        <AnimatePresence mode="popLayout">
          <motion.span
            key={padded}
            initial={{ opacity: 0, y: 14, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -14, filter: "blur(4px)" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="block font-serif italic font-light text-ink text-5xl sm:text-6xl text-center tabular-nums"
          >
            {padded}
          </motion.span>
        </AnimatePresence>
      </div>
      <span className="mt-2 text-[10px] sm:text-xs tracking-[0.35em] uppercase text-ink/55">
        {label}
      </span>
    </div>
  );
}

export default function Countdown() {
  const [time, setTime] = useState(getRemaining());

  useEffect(() => {
    const id = setInterval(() => setTime(getRemaining()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="countdown"
      data-testid="countdown-section"
      className="relative py-20 px-6 text-center"
    >
      <SectionReveal>
        <p className="uppercase text-[11px] tracking-[0.45em] text-ink/55 mb-3">
          Counting the moments
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl italic text-ink mb-2">
          Until we say <span className="shimmer-gold">Qabūl Hai</span>
        </h2>
        <p className="font-arabic text-xl text-champagne mt-3 mb-1">
          قُلْتُ قَبِلْتُ
        </p>
        <p className="text-xs sm:text-sm italic text-ink/60 tracking-wide">
          &ldquo;I accept&rdquo; — the words that seal the Nikah
        </p>
        <div className="mb-10" />
      </SectionReveal>

      <SectionReveal delay={0.2}>
        <div
          className="glass-card rounded-[28px] px-4 py-8 flex items-start gap-2 sm:gap-4 max-w-md mx-auto"
          data-testid="countdown-grid"
        >
          <Digit value={time.days} label="Days" testid="countdown-days" />
          <span className="font-serif text-4xl text-champagne/50 translate-y-2">
            :
          </span>
          <Digit value={time.hours} label="Hours" testid="countdown-hours" />
          <span className="font-serif text-4xl text-champagne/50 translate-y-2">
            :
          </span>
          <Digit
            value={time.minutes}
            label="Minutes"
            testid="countdown-minutes"
          />
          <span className="font-serif text-4xl text-champagne/50 translate-y-2">
            :
          </span>
          <Digit
            value={time.seconds}
            label="Seconds"
            testid="countdown-seconds"
          />
        </div>
      </SectionReveal>

      <SectionReveal delay={0.4}>
        <Divider />
      </SectionReveal>
    </section>
  );
}
